import React, { useState, useEffect } from 'react';
import { 
  User, 
  Building2, 
  CreditCard, 
  DollarSign, 
  FileText, 
  Save, 
  Plus, 
  Trash2, 
  AlertCircle,
  CheckCircle,
  Loader2,
  Calendar,
  Phone,
  Mail,
  MapPin,
  Users,
  Car,
  Home,
  TrendingUp
} from 'lucide-react';
import { DatabaseService } from '../lib/database';
import type { 
  BuyerFinancialProfile,
  CreditScore,
  BankAccount,
  Asset,
  Liability,
  IncomeSource,
  TaxReturn,
  SBAPersonalFinancialStatement
} from '../types/buyerFinancialData';

interface BuyerProfileFormProps {
  buyerId: string;
  buyerEmail: string;
  onProfileUpdate: () => void;
}

export function BuyerProfileForm({ buyerId, buyerEmail, onProfileUpdate }: BuyerProfileFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<'personal' | 'financial' | 'assets' | 'liabilities' | 'income' | 'taxes'>('personal');

  // Empty profile template
  const emptyProfile: Partial<BuyerFinancialProfile> = {
    buyerId,
    profileDate: new Date().toISOString().split('T')[0],
    creditScores: [],
    bankAccounts: [],
    sbaPersonalFinancialStatement: {
      id: '',
      statementDate: new Date().toISOString().split('T')[0],
      personalInfo: {
        name: '',
        ssn: '',
        address: '',
        phone: '',
        email: buyerEmail,
        maritalStatus: 'single',
        dependents: 0
      },
      assets: [],
      liabilities: [],
      incomeSources: [],
      monthlyNetWorth: 0,
      debtToIncomeRatio: 0,
      liquidityRatio: 0
    },
    taxReturns: []
  };

  // Form state
  const [profile, setProfile] = useState<Partial<BuyerFinancialProfile>>(emptyProfile);

  // Load existing profile data
  useEffect(() => {
    loadProfile();
  }, [buyerId]);

  const loadProfile = async () => {
    setIsLoading(true);
    try {
      const response = await DatabaseService.getBuyerFinancialProfile(buyerId);
      if (response.error) {
        setError(response.error);
      } else if (response.data) {
        // Transform database data to match the expected format
        const transformedProfile = {
          ...emptyProfile,
          buyerId: response.data.creator,
          profileDate: response.data.profile_date,
          totalAssets: response.data.total_assets || 0,
          totalLiabilities: response.data.total_liabilities || 0,
          netWorth: response.data.net_worth || 0,
          monthlyIncome: response.data.monthly_income || 0,
          monthlyExpenses: response.data.monthly_expenses || 0,
          monthlyNetIncome: response.data.monthly_net_income || 0,
          debtToIncomeRatio: response.data.debt_to_income_ratio || 0,
          creditUtilizationRatio: response.data.credit_utilization_ratio || 0,
          liquidityRatio: response.data.liquidity_ratio || 0,
          riskScore: response.data.risk_score || 0,
          sbaEligibilityScore: response.data.sba_eligibility_score || 0,
          lastUpdated: response.data.last_updated,
          creditScores: response.data.creditScores?.map((score: any) => ({
            bureau: score.bureau,
            score: score.score,
            date: score.score_date,
            factors: {
              paymentHistory: score.payment_history,
              creditUtilization: score.credit_utilization,
              lengthOfCreditHistory: score.length_of_credit_history,
              newCredit: score.new_credit,
              creditMix: score.credit_mix
            }
          })) || [],
          bankAccounts: response.data.bankAccounts?.map((account: any) => ({
            id: account.id,
            accountType: account.account_type,
            bankName: account.bank_name,
            accountNumber: account.account_number,
            currentBalance: account.current_balance,
            averageBalance: account.average_balance,
            monthlyInflow: account.monthly_inflow,
            monthlyOutflow: account.monthly_outflow,
            statementDate: account.statement_date
          })) || [],
          sbaPersonalFinancialStatement: {
            ...emptyProfile.sbaPersonalFinancialStatement,
            personalInfo: response.data.sbaPersonalFinancialStatement?.personal_info || {},
            assets: response.data.assets?.map((asset: any) => ({
              type: asset.asset_type,
              description: asset.description,
              value: asset.current_value,
              isLiquid: asset.is_liquid,
              monthlyIncome: asset.monthly_income
            })) || [],
            liabilities: response.data.liabilities?.map((liability: any) => ({
              type: liability.liability_type,
              description: liability.description,
              balance: liability.current_balance,
              monthlyPayment: liability.monthly_payment,
              interestRate: liability.interest_rate
            })) || [],
            incomeSources: response.data.incomeSources?.map((income: any) => ({
              type: income.income_type,
              description: income.description,
              monthlyAmount: income.monthly_amount,
              annualAmount: income.annual_amount,
              isGuaranteed: income.is_guaranteed,
              startDate: income.start_date,
              employer: income.employer
            })) || []
          },
          taxReturns: response.data.taxReturns?.map((taxReturn: any) => ({
            year: taxReturn.tax_year,
            filingStatus: taxReturn.filing_status,
            adjustedGrossIncome: taxReturn.adjusted_gross_income,
            taxableIncome: taxReturn.taxable_income,
            totalTax: taxReturn.total_tax,
            w2Income: taxReturn.w2_income,
            businessIncome: taxReturn.business_income,
            investmentIncome: taxReturn.investment_income,
            otherIncome: taxReturn.other_income,
            isFiled: taxReturn.is_filed,
            filingDate: taxReturn.filing_date
          })) || []
        };
        setProfile(transformedProfile);
      } else {
        setProfile(emptyProfile);
      }
    } catch (err) {
      console.error('Load profile error:', err);
      setError('Failed to load profile data');
    } finally {
      setIsLoading(false);
    }
  };

  const saveProfile = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const response = await DatabaseService.saveBuyerFinancialProfile(buyerId, profile);
      if (response.error) {
        console.error('Save profile error:', response.error);
        setError(response.error);
      } else {
        setMessage('Profile saved successfully');
        setTimeout(() => setMessage(null), 3000);
        onProfileUpdate();
      }
    } catch (err) {
      console.error('Save profile error:', err);
      setError('Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  };

  const updatePersonalInfo = (field: string, value: any) => {
    setProfile(prev => ({
      ...prev,
      sbaPersonalFinancialStatement: {
        ...prev.sbaPersonalFinancialStatement!,
        personalInfo: {
          ...prev.sbaPersonalFinancialStatement!.personalInfo,
          [field]: value
        }
      }
    }));
  };

  const addCreditScore = () => {
    const newScore: CreditScore = {
      bureau: 'Experian',
      score: 0,
      date: new Date().toISOString().split('T')[0]
    };
    setProfile(prev => ({
      ...prev,
      creditScores: [...(prev.creditScores || []), newScore]
    }));
  };

  const updateCreditScore = (index: number, field: string, value: any) => {
    setProfile(prev => ({
      ...prev,
      creditScores: prev.creditScores?.map((score, i) => 
        i === index ? { ...score, [field]: value } : score
      ) || []
    }));
  };

  const removeCreditScore = (index: number) => {
    setProfile(prev => ({
      ...prev,
      creditScores: prev.creditScores?.filter((_, i) => i !== index) || []
    }));
  };

  const addBankAccount = () => {
    const newAccount: BankAccount = {
      id: `account-${Date.now()}`,
      accountType: 'checking',
      bankName: '',
      accountNumber: '',
      currentBalance: 0,
      averageBalance: 0,
      monthlyInflow: 0,
      monthlyOutflow: 0,
      statementDate: new Date().toISOString().split('T')[0]
    };
    setProfile(prev => ({
      ...prev,
      bankAccounts: [...(prev.bankAccounts || []), newAccount]
    }));
  };

  const updateBankAccount = (index: number, field: string, value: any) => {
    setProfile(prev => ({
      ...prev,
      bankAccounts: prev.bankAccounts?.map((account, i) => 
        i === index ? { ...account, [field]: value } : account
      ) || []
    }));
  };

  const removeBankAccount = (index: number) => {
    setProfile(prev => ({
      ...prev,
      bankAccounts: prev.bankAccounts?.filter((_, i) => i !== index) || []
    }));
  };

  const addAsset = () => {
    const newAsset: Asset = {
      id: `asset-${Date.now()}`,
      type: 'real_estate',
      description: '',
      currentValue: 0,
      isLiquid: false,
      isCollateral: false
    };
    setProfile(prev => ({
      ...prev,
      sbaPersonalFinancialStatement: {
        ...prev.sbaPersonalFinancialStatement!,
        assets: [...(prev.sbaPersonalFinancialStatement?.assets || []), newAsset]
      }
    }));
  };

  const updateAsset = (index: number, field: string, value: any) => {
    setProfile(prev => ({
      ...prev,
      sbaPersonalFinancialStatement: {
        ...prev.sbaPersonalFinancialStatement!,
        assets: prev.sbaPersonalFinancialStatement?.assets.map((asset, i) => 
          i === index ? { ...asset, [field]: value } : asset
        ) || []
      }
    }));
  };

  const removeAsset = (index: number) => {
    setProfile(prev => ({
      ...prev,
      sbaPersonalFinancialStatement: {
        ...prev.sbaPersonalFinancialStatement!,
        assets: prev.sbaPersonalFinancialStatement?.assets.filter((_, i) => i !== index) || []
      }
    }));
  };

  const addLiability = () => {
    const newLiability: Liability = {
      id: `liability-${Date.now()}`,
      type: 'mortgage',
      description: '',
      originalAmount: 0,
      currentBalance: 0,
      monthlyPayment: 0,
      interestRate: 0,
      termMonths: 0,
      remainingMonths: 0,
      isBusinessRelated: false
    };
    setProfile(prev => ({
      ...prev,
      sbaPersonalFinancialStatement: {
        ...prev.sbaPersonalFinancialStatement!,
        liabilities: [...(prev.sbaPersonalFinancialStatement?.liabilities || []), newLiability]
      }
    }));
  };

  const updateLiability = (index: number, field: string, value: any) => {
    setProfile(prev => ({
      ...prev,
      sbaPersonalFinancialStatement: {
        ...prev.sbaPersonalFinancialStatement!,
        liabilities: prev.sbaPersonalFinancialStatement?.liabilities.map((liability, i) => 
          i === index ? { ...liability, [field]: value } : liability
        ) || []
      }
    }));
  };

  const removeLiability = (index: number) => {
    setProfile(prev => ({
      ...prev,
      sbaPersonalFinancialStatement: {
        ...prev.sbaPersonalFinancialStatement!,
        liabilities: prev.sbaPersonalFinancialStatement?.liabilities.filter((_, i) => i !== index) || []
      }
    }));
  };

  const addIncomeSource = () => {
    const newIncome: IncomeSource = {
      id: `income-${Date.now()}`,
      type: 'salary',
      description: '',
      monthlyAmount: 0,
      annualAmount: 0,
      isGuaranteed: true,
      startDate: new Date().toISOString().split('T')[0]
    };
    setProfile(prev => ({
      ...prev,
      sbaPersonalFinancialStatement: {
        ...prev.sbaPersonalFinancialStatement!,
        incomeSources: [...(prev.sbaPersonalFinancialStatement?.incomeSources || []), newIncome]
      }
    }));
  };

  const updateIncomeSource = (index: number, field: string, value: any) => {
    setProfile(prev => ({
      ...prev,
      sbaPersonalFinancialStatement: {
        ...prev.sbaPersonalFinancialStatement!,
        incomeSources: prev.sbaPersonalFinancialStatement?.incomeSources.map((income, i) => 
          i === index ? { ...income, [field]: value } : income
        ) || []
      }
    }));
  };

  const removeIncomeSource = (index: number) => {
    setProfile(prev => ({
      ...prev,
      sbaPersonalFinancialStatement: {
        ...prev.sbaPersonalFinancialStatement!,
        incomeSources: prev.sbaPersonalFinancialStatement?.incomeSources.filter((_, i) => i !== index) || []
      }
    }));
  };

  const addTaxReturn = () => {
    const newTaxReturn: TaxReturn = {
      year: new Date().getFullYear() - 1,
      filingStatus: 'single',
      adjustedGrossIncome: 0,
      taxableIncome: 0,
      totalTax: 0,
      refund: 0,
      w2Income: 0,
      businessIncome: 0,
      investmentIncome: 0,
      otherIncome: 0,
      deductions: 0,
      exemptions: 0,
      isFiled: false
    };
    setProfile(prev => ({
      ...prev,
      taxReturns: [...(prev.taxReturns || []), newTaxReturn]
    }));
  };

  const updateTaxReturn = (index: number, field: string, value: any) => {
    setProfile(prev => ({
      ...prev,
      taxReturns: prev.taxReturns?.map((taxReturn, i) => 
        i === index ? { ...taxReturn, [field]: value } : taxReturn
      ) || []
    }));
  };

  const removeTaxReturn = (index: number) => {
    setProfile(prev => ({
      ...prev,
      taxReturns: prev.taxReturns?.filter((_, i) => i !== index) || []
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
        <span className="ml-2 text-gray-600">Loading profile...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Messages */}
      {message && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-800">{message}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Section Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6 overflow-x-auto">
            {[
              { id: 'personal', label: 'Personal Info', icon: User },
              { id: 'financial', label: 'Credit & Banking', icon: CreditCard },
              { id: 'assets', label: 'Assets', icon: Home },
              { id: 'liabilities', label: 'Liabilities', icon: TrendingUp },
              { id: 'income', label: 'Income Sources', icon: DollarSign },
              { id: 'taxes', label: 'Tax Returns', icon: FileText }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveSection(id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeSection === id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  {label}
                </div>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Section Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        {activeSection === 'personal' && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <User className="w-5 h-5 text-purple-600" />
              Personal Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={profile.sbaPersonalFinancialStatement?.personalInfo.name || ''}
                  onChange={(e) => updatePersonalInfo('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter full name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SSN (Last 4 digits) *
                </label>
                <input
                  type="text"
                  value={profile.sbaPersonalFinancialStatement?.personalInfo.ssn || ''}
                  onChange={(e) => updatePersonalInfo('ssn', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="****-****-1234"
                  maxLength={4}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={profile.sbaPersonalFinancialStatement?.personalInfo.phone || ''}
                  onChange={(e) => updatePersonalInfo('phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="(555) 123-4567"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={profile.sbaPersonalFinancialStatement?.personalInfo.email || ''}
                  onChange={(e) => updatePersonalInfo('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="email@example.com"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address *
                </label>
                <input
                  type="text"
                  value={profile.sbaPersonalFinancialStatement?.personalInfo.address || ''}
                  onChange={(e) => updatePersonalInfo('address', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="123 Main St, City, State 12345"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Marital Status *
                </label>
                <select
                  value={profile.sbaPersonalFinancialStatement?.personalInfo.maritalStatus || 'single'}
                  onChange={(e) => updatePersonalInfo('maritalStatus', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                  <option value="divorced">Divorced</option>
                  <option value="widowed">Widowed</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Dependents
                </label>
                <input
                  type="number"
                  min="0"
                  value={profile.sbaPersonalFinancialStatement?.personalInfo.dependents || 0}
                  onChange={(e) => updatePersonalInfo('dependents', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          </div>
        )}

        {activeSection === 'financial' && (
          <div className="space-y-8">
            {/* Credit Scores */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-purple-600" />
                  Credit Scores
                </h3>
                <button
                  onClick={addCreditScore}
                  className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Credit Score
                </button>
              </div>
              
              <div className="space-y-4">
                {profile.creditScores?.map((score, index) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-900">Credit Score #{index + 1}</h4>
                      <button
                        onClick={() => removeCreditScore(index)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Credit Bureau
                        </label>
                        <select
                          value={score.bureau}
                          onChange={(e) => updateCreditScore(index, 'bureau', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="Experian">Experian</option>
                          <option value="Equifax">Equifax</option>
                          <option value="TransUnion">TransUnion</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Score
                        </label>
                        <input
                          type="number"
                          min="300"
                          max="850"
                          value={score.score}
                          onChange={(e) => updateCreditScore(index, 'score', parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Date
                        </label>
                        <input
                          type="date"
                          value={score.date}
                          onChange={(e) => updateCreditScore(index, 'date', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bank Accounts */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-purple-600" />
                  Bank Accounts
                </h3>
                <button
                  onClick={addBankAccount}
                  className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Bank Account
                </button>
              </div>
              
              <div className="space-y-4">
                {profile.bankAccounts?.map((account, index) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-900">Account #{index + 1}</h4>
                      <button
                        onClick={() => removeBankAccount(index)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Account Type
                        </label>
                        <select
                          value={account.accountType}
                          onChange={(e) => updateBankAccount(index, 'accountType', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="checking">Checking</option>
                          <option value="savings">Savings</option>
                          <option value="money_market">Money Market</option>
                          <option value="cd">Certificate of Deposit</option>
                          <option value="investment">Investment</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Bank Name
                        </label>
                        <input
                          type="text"
                          value={account.bankName}
                          onChange={(e) => updateBankAccount(index, 'bankName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="Bank Name"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Account Number (Last 4)
                        </label>
                        <input
                          type="text"
                          value={account.accountNumber}
                          onChange={(e) => updateBankAccount(index, 'accountNumber', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="****1234"
                          maxLength={8}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Current Balance
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={account.currentBalance}
                          onChange={(e) => updateBankAccount(index, 'currentBalance', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Average Balance
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={account.averageBalance}
                          onChange={(e) => updateBankAccount(index, 'averageBalance', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Statement Date
                        </label>
                        <input
                          type="date"
                          value={account.statementDate}
                          onChange={(e) => updateBankAccount(index, 'statementDate', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Continue with other sections... */}
        {activeSection === 'assets' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Home className="w-5 h-5 text-purple-600" />
                Assets
              </h3>
              <button
                onClick={addAsset}
                className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Asset
              </button>
            </div>
            
            <div className="space-y-4">
              {profile.sbaPersonalFinancialStatement?.assets?.map((asset, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-gray-900">Asset #{index + 1}</h4>
                    <button
                      onClick={() => removeAsset(index)}
                      className="text-red-600 hover:text-red-800 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Asset Type
                      </label>
                      <select
                        value={asset.type}
                        onChange={(e) => updateAsset(index, 'type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="real_estate">Real Estate</option>
                        <option value="vehicle">Vehicle</option>
                        <option value="investment">Investment</option>
                        <option value="business">Business</option>
                        <option value="personal_property">Personal Property</option>
                        <option value="cash">Cash</option>
                        <option value="retirement">Retirement</option>
                      </select>
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <input
                        type="text"
                        value={asset.description}
                        onChange={(e) => updateAsset(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Asset description"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current Value
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={asset.currentValue}
                        onChange={(e) => updateAsset(index, 'currentValue', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={asset.isLiquid}
                          onChange={(e) => updateAsset(index, 'isLiquid', e.target.checked)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">Liquid Asset</span>
                      </label>
                      
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={asset.isCollateral}
                          onChange={(e) => updateAsset(index, 'isCollateral', e.target.checked)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">Can be Collateral</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Liabilities Section */}
        {activeSection === 'liabilities' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                Liabilities
              </h3>
              <button
                onClick={addLiability}
                className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Liability
              </button>
            </div>
            
            <div className="space-y-4">
              {profile.sbaPersonalFinancialStatement?.liabilities?.map((liability, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-gray-900">Liability #{index + 1}</h4>
                    <button
                      onClick={() => removeLiability(index)}
                      className="text-red-600 hover:text-red-800 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Liability Type
                      </label>
                      <select
                        value={liability.type}
                        onChange={(e) => updateLiability(index, 'type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="mortgage">Mortgage</option>
                        <option value="auto_loan">Auto Loan</option>
                        <option value="credit_card">Credit Card</option>
                        <option value="personal_loan">Personal Loan</option>
                        <option value="student_loan">Student Loan</option>
                        <option value="business_loan">Business Loan</option>
                        <option value="home_equity">Home Equity</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <input
                        type="text"
                        value={liability.description}
                        onChange={(e) => updateLiability(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Liability description"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Original Amount
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={liability.originalAmount}
                        onChange={(e) => updateLiability(index, 'originalAmount', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current Balance
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={liability.currentBalance}
                        onChange={(e) => updateLiability(index, 'currentBalance', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Monthly Payment
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={liability.monthlyPayment}
                        onChange={(e) => updateLiability(index, 'monthlyPayment', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Interest Rate (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={liability.interestRate}
                        onChange={(e) => updateLiability(index, 'interestRate', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Term (Months)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={liability.termMonths}
                        onChange={(e) => updateLiability(index, 'termMonths', parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Remaining Months
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={liability.remainingMonths}
                        onChange={(e) => updateLiability(index, 'remainingMonths', parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={liability.isBusinessRelated}
                          onChange={(e) => updateLiability(index, 'isBusinessRelated', e.target.checked)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">Business Related</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Income Sources Section */}
        {activeSection === 'income' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-purple-600" />
                Income Sources
              </h3>
              <button
                onClick={addIncomeSource}
                className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Income Source
              </button>
            </div>
            
            <div className="space-y-4">
              {profile.sbaPersonalFinancialStatement?.incomeSources?.map((income, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-gray-900">Income Source #{index + 1}</h4>
                    <button
                      onClick={() => removeIncomeSource(index)}
                      className="text-red-600 hover:text-red-800 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Income Type
                      </label>
                      <select
                        value={income.type}
                        onChange={(e) => updateIncomeSource(index, 'type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="salary">Salary</option>
                        <option value="business_income">Business Income</option>
                        <option value="investment">Investment</option>
                        <option value="rental">Rental</option>
                        <option value="pension">Pension</option>
                        <option value="social_security">Social Security</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <input
                        type="text"
                        value={income.description}
                        onChange={(e) => updateIncomeSource(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Income source description"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Monthly Amount
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={income.monthlyAmount}
                        onChange={(e) => updateIncomeSource(index, 'monthlyAmount', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Annual Amount
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={income.annualAmount}
                        onChange={(e) => updateIncomeSource(index, 'annualAmount', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={income.startDate}
                        onChange={(e) => updateIncomeSource(index, 'startDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={income.endDate || ''}
                        onChange={(e) => updateIncomeSource(index, 'endDate', e.target.value || null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Employer
                      </label>
                      <input
                        type="text"
                        value={income.employer || ''}
                        onChange={(e) => updateIncomeSource(index, 'employer', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Employer name"
                      />
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={income.isGuaranteed}
                          onChange={(e) => updateIncomeSource(index, 'isGuaranteed', e.target.checked)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">Guaranteed Income</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tax Returns Section */}
        {activeSection === 'taxes' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                Tax Returns
              </h3>
              <button
                onClick={addTaxReturn}
                className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Tax Return
              </button>
            </div>
            
            <div className="space-y-4">
              {profile.taxReturns?.map((taxReturn, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-gray-900">Tax Year {taxReturn.year}</h4>
                    <button
                      onClick={() => removeTaxReturn(index)}
                      className="text-red-600 hover:text-red-800 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tax Year
                      </label>
                      <input
                        type="number"
                        min="1900"
                        max="2100"
                        value={taxReturn.year}
                        onChange={(e) => updateTaxReturn(index, 'year', parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Filing Status
                      </label>
                      <select
                        value={taxReturn.filingStatus}
                        onChange={(e) => updateTaxReturn(index, 'filingStatus', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="single">Single</option>
                        <option value="married_joint">Married Filing Jointly</option>
                        <option value="married_separate">Married Filing Separately</option>
                        <option value="head_of_household">Head of Household</option>
                        <option value="qualifying_widow">Qualifying Widow(er)</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Filing Date
                      </label>
                      <input
                        type="date"
                        value={taxReturn.filingDate || ''}
                        onChange={(e) => updateTaxReturn(index, 'filingDate', e.target.value || null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Adjusted Gross Income
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={taxReturn.adjustedGrossIncome}
                        onChange={(e) => updateTaxReturn(index, 'adjustedGrossIncome', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Taxable Income
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={taxReturn.taxableIncome}
                        onChange={(e) => updateTaxReturn(index, 'taxableIncome', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Total Tax
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={taxReturn.totalTax}
                        onChange={(e) => updateTaxReturn(index, 'totalTax', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Refund
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={taxReturn.refund}
                        onChange={(e) => updateTaxReturn(index, 'refund', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        W-2 Income
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={taxReturn.w2Income}
                        onChange={(e) => updateTaxReturn(index, 'w2Income', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Business Income
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={taxReturn.businessIncome}
                        onChange={(e) => updateTaxReturn(index, 'businessIncome', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Investment Income
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={taxReturn.investmentIncome}
                        onChange={(e) => updateTaxReturn(index, 'investmentIncome', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Other Income
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={taxReturn.otherIncome}
                        onChange={(e) => updateTaxReturn(index, 'otherIncome', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Deductions
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={taxReturn.deductions}
                        onChange={(e) => updateTaxReturn(index, 'deductions', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Exemptions
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={taxReturn.exemptions}
                        onChange={(e) => updateTaxReturn(index, 'exemptions', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={taxReturn.isFiled}
                          onChange={(e) => updateTaxReturn(index, 'isFiled', e.target.checked)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">Filed</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveProfile}
          disabled={isSaving}
          className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {isSaving ? 'Saving...' : 'Save Profile'}
        </button>
      </div>
    </div>
  );
}
